import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useBetaSignup } from '../context/BetaSignupContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { TrendingUp, Search, Globe, Wallet, LineChart, MessageCircle, Shield, Smartphone, ArrowRight, Lock, Compass, Zap, Sparkles, Bot, Unlock, Eye, Briefcase, Target, PieChart, ChevronDown, ChevronUp } from 'lucide-react';
import { investmentStrategies, features } from '../mockData';

// Why Trove Features Component with Expandable Text
const WhyTroveFeatures = () => {
  const [expandedFeature, setExpandedFeature] = useState(null);

  const features = [
    {
      id: 1,
      icon: TrendingUp,
      title: 'Automatic Portfolio Rebalancing',
      shortText: 'Intelligence rebalancing based on multiple factors such as sectors, markets, weighted risk score.',
      fullText: 'Intelligence rebalancing based on multiple factors such as sectors, markets, weighted risk score. Trove removes the stress, knowledge and time it takes to ensure your portfolio is always optimised for success.'
    },
    {
      id: 2,
      icon: PieChart,
      title: 'Multi-Asset Strategy for the modern investor',
      shortText: 'Your portfolio is powered by high-quality, resilient assets.',
      fullText: 'Your portfolio is powered by high-quality, resilient assets. Trove offers a variety of assets, ranging from real estate, private equity to venture capital to private credit and of course equities and bonds. Historically, these kinds of investments would\'ve been reserved exclusively for institutional investors with lengthy lock ups and high minimum ticket sizes.'
    },
    {
      id: 3,
      icon: Wallet,
      title: 'Monthly Liquidity',
      shortText: 'You can redeem some or all of your shares, subject to limitations, at any point.',
      fullText: 'You can redeem some or all of your shares, subject to limitations, at any point, and redemptions are processed at the beginning of each month. Thanks to our redemption program, you aren\'t reliant on an unpredictable secondary market, and—most importantly—you don\'t have to trade flexibility for the long-term benefits of private market investing.'
    },
    {
      id: 4,
      icon: Bot,
      title: 'Automate & Accumulate',
      shortText: 'We\'ve redesigned each step of the investment management process.',
      fullText: 'We\'ve redesigned each step of the investment management process, replacing high-cost manual workflows with software-enabled automated systems. The result is a single integrated platform designed to deliver better risk adjusted returns and performance potential.'
    }
  ];

  const toggleExpand = (id) => {
    setExpandedFeature(expandedFeature === id ? null : id);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      {features.map((feature) => {
        const IconComponent = feature.icon;
        const isExpanded = expandedFeature === feature.id;
        
        return (
          <Card key={feature.id} className="p-8 border-0 shadow-sm hover:shadow-lg transition-shadow">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                <IconComponent className="text-[#1a1a3e]" size={28} />
              </div>
              <h3 className="text-xl font-light flex-1" style={{ fontFamily: 'Georgia, serif', color: '#1a1a3e' }}>
                {feature.title}
              </h3>
            </div>
            <p className="text-gray-600 font-light leading-relaxed mb-4">
              {isExpanded ? feature.fullText : feature.shortText}
            </p>
            <button
              onClick={() => toggleExpand(feature.id)}
              className="flex items-center gap-2 text-[#FF8C42] hover:text-[#FFB366] font-medium text-sm transition-colors"
              data-testid={`feature-expand-${feature.id}`}
            >
              {isExpanded ? (
                <>
                  Show Less <ChevronUp size={16} />
                </>
              ) : (
                <>
                  Read More <ChevronDown size={16} />
                </>
              )}
            </button>
          </Card>
        );
      })}
    </div>
  );
};

const Home = () => {
  const navigate = useNavigate();
  const { openModal } = useBetaSignup();

  const iconMap = {
    TrendingUp: TrendingUp,
    Search: Search,
    Globe: Globe,
    Wallet: Wallet,
    LineChart: LineChart,
    MessageCircle: MessageCircle,
    Shield: Shield,
    Smartphone: Smartphone,
    Lock: Lock,
    Compass: Compass,
    Zap: Zap,
    Sparkles: Sparkles,
    Bot: Bot,
    Unlock: Unlock
  };

  // Handle strategy card click - navigate to offerings page with anchor
  const handleStrategyClick = (strategyId) => {
    navigate(`/offerings#${strategyId}`);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 px-4 sm:px-6 lg:px-8 bg-[#F5F3ED]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block mb-8 px-6 py-3 bg-[#FF8C42] text-white text-sm font-medium rounded-full">
              Multi-asset wealth platform built for HENWY's
            </div>
            <h1 className="text-6xl md:text-7xl font-light tracking-tight mb-8 leading-tight" style={{ fontFamily: 'Georgia, serif' }}>
              Tearing down barriers<br />
              to the best investments.
            </h1>
            <p className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto font-light">
              Hedge fund-grade portfolios, straightforward fees, for today's investor.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-[#1a1a3e] hover:bg-[#252550] px-10 py-7 text-base rounded-full"
                onClick={openModal}
                data-testid="hero-start-investing-btn"
              >
                Start Investing
              </Button>
              <Link to="/offerings">
                <Button size="lg" variant="outline" className="px-10 py-7 text-base border-2 border-[#1a1a3e] hover:bg-[#1a1a3e] hover:text-white rounded-full">
                  Explore Strategies
                </Button>
              </Link>
            </div>
          </div>

          {/* Trust Metrics - Removed AUM and performance stats, kept fee */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mt-20">
            <div className="text-center">
              <div className="text-5xl font-light mb-3" style={{ fontFamily: 'Georgia, serif' }}>6</div>
              <div className="text-sm uppercase tracking-wider text-gray-500">Investment Strategies</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-light mb-3" style={{ fontFamily: 'Georgia, serif' }}>$500</div>
              <div className="text-sm uppercase tracking-wider text-gray-500">Minimum Investment</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-light mb-3" style={{ fontFamily: 'Georgia, serif' }}>0.8-1.5%</div>
              <div className="text-sm uppercase tracking-wider text-gray-500">Advisory Fee</div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Trove Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-sm uppercase tracking-widest text-gray-500 mb-4">WHY TROVE</div>
            <h2 className="text-5xl md:text-6xl font-light mb-6" style={{ fontFamily: 'Georgia, serif' }}>
              Wealth building<br />simplified
            </h2>
          </div>

          <WhyTroveFeatures />
        </div>
      </section>

      {/* Investment Strategies - Click routes to Offerings with anchor */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#F5F3ED]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-light mb-6" style={{ fontFamily: 'Georgia, serif' }}>
              Six strategies.<br />One platform.
            </h2>
            <p className="text-xl text-gray-600 font-light max-w-2xl mx-auto">
              From conservative wealth preservation to aggressive growth, we have portfolio strategies designed for your goals.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {investmentStrategies.map((strategy) => {
              const IconComponent = iconMap[strategy.icon];
              return (
                <Card 
                  key={strategy.id} 
                  onClick={() => handleStrategyClick(strategy.id)}
                  className="h-full p-8 border-2 border-gray-200 shadow-md hover:shadow-2xl transition-all hover:-translate-y-2 cursor-pointer group rounded-2xl"
                  data-testid={`strategy-card-${strategy.id}`}
                >
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-16 h-16 rounded-lg flex items-center justify-center">
                      <IconComponent className="text-[#1a1a3e]" size={36} />
                    </div>
                    <div className="px-4 py-2 bg-[#FF8C42] rounded-full">
                      <div className="text-lg font-semibold text-white" style={{ fontFamily: 'Georgia, serif' }}>
                        {strategy.targetReturn}
                      </div>
                    </div>
                  </div>
                  <h3 className="text-2xl font-light mb-3" style={{ fontFamily: 'Georgia, serif', color: '#1a1a3e' }}>
                    {strategy.name}
                  </h3>
                  <p className="text-sm uppercase tracking-wider text-gray-500 mb-4">
                    {strategy.category}
                  </p>
                  <p className="text-gray-600 font-light leading-relaxed">
                    {strategy.description}
                  </p>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/offerings">
              <Button size="lg" className="bg-[#1a1a3e] hover:bg-[#252550] px-10 py-6 text-base rounded-full">
                View All Strategies
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Transparent Pricing */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-5xl md:text-6xl font-light mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            Transparent Pricing
          </h2>
          <p className="text-xl text-gray-600 font-light max-w-2xl mx-auto mb-12">
            Our fee structure is designed to align with your success. We only win when you win.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="p-8 border-2 border-gray-200 shadow-lg rounded-2xl">
              <h3 className="text-3xl font-light mb-4" style={{ fontFamily: 'Georgia, serif' }}>Management Fee</h3>
              <div className="text-5xl font-light mb-4" style={{ fontFamily: 'Georgia, serif' }}>0.8-1.5%</div>
              <p className="text-gray-600 font-light mb-6">Based on portfolio complexity</p>
              <ul className="text-left space-y-2 text-sm text-gray-600 font-light">
                <li className="flex items-start"><span className="mr-2">•</span><span>Simple portfolios: 0.8%</span></li>
                <li className="flex items-start"><span className="mr-2">•</span><span>Balanced portfolios: 1.2%</span></li>
                <li className="flex items-start"><span className="mr-2">•</span><span>Complex portfolios: 1.5%</span></li>
              </ul>
            </Card>
            
            <Card className="p-8 border-2 border-gray-200 shadow-lg rounded-2xl">
              <h3 className="text-3xl font-light mb-4" style={{ fontFamily: 'Georgia, serif' }}>Success Fee</h3>
              <div className="text-5xl font-light mb-4" style={{ fontFamily: 'Georgia, serif' }}>10-20%</div>
              <p className="text-gray-600 font-light mb-6">Of returns above benchmark</p>
              <ul className="text-left space-y-2 text-sm text-gray-600 font-light">
                <li className="flex items-start"><span className="mr-2">•</span><span>Only on outperformance</span></li>
                <li className="flex items-start"><span className="mr-2">•</span><span>High-water mark protection</span></li>
                <li className="flex items-start"><span className="mr-2">•</span><span>We win when you win</span></li>
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#1a1a3e] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-5xl md:text-6xl font-light mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            Ready to grow<br />your wealth?
          </h2>
          <p className="text-xl text-gray-300 font-light mb-12 max-w-2xl mx-auto">
            Join thousands of Australians who have bridged the gap from high income to high net worth.
          </p>
          <Button 
            size="lg" 
            className="bg-[#FF8C42] hover:bg-[#FFB366] text-white px-12 py-7 text-lg rounded-full"
            onClick={openModal}
            data-testid="footer-cta-btn"
          >
            Start Investing Today
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Home;
